/**
 * Browser entry: Flash-compatible runtime on a <canvas>, extracted assets, own backend, and the ported game.
 *
 * Backend (src/client/config.ts): by default the server the page came from (WebSocket `/ws` of the same origin: the
 * Vite dev proxy or the self-hosted server); `?backend=offline` (or a build with VITE_FWP_BACKEND=offline, such as the
 * static Cloudflare Pages demo) plays with the in-process backend in localStorage, `?backend=memory` without
 * persistence, `?backend=http://host:8787` / `ws://host:8787/ws` picks another server. An unavailable server is never
 * replaced by offline play: the loading status shows the address and retries with backoff (a click or a key press
 * retries at once).
 *
 * Accounts: the game restores the session stored for the backend's origin; without one it shows its sign-in screen
 * (nickname + password, src/game/ui/auth-screen.ts). A restart of the game (language switch, sign-out, an ended session)
 * reloads the page; its reason survives the reload in sessionStorage, so the sign-in screen can say e.g. that the
 * session expired.
 *
 * Wide screens (src/shared/wide-view.ts, src/game/ui/wide-screen.ts): in touch mode and in the app the 807x600 window
 * is as tall as the screen and the margin around it (up to ~22:9) is shown instead of letterbox bars; every scene has
 * content there. The canvas covers the whole screen (notches too: their insets keep the taps clear) and an on-screen
 * keyboard never resizes it: the focused field is shown above the keyboard (src/client/platform/viewport.ts).
 *
 * Mobile and the platform adapter (src/client/platform): persistent data goes through the app storage (Capacitor
 * Preferences in the native app, else localStorage; no cookies); the server is the page's origin on the web, else the
 * player's server setting (the native app, the dev server: the sign-in screen's "Server" view or the loading screen's
 * server form) or the build's VITE_BACKEND_URL; the app pauses in the background (frame loop, audio) and wakes the
 * connection on return (an immediate reconnect, auth.resume / auth.refresh); the Android back button closes the top
 * screen like its close button and asks before leaving the game; touch screens get on-screen battle controls that
 * send the keyboard's keys, and the help texts describe those controls instead of keys and the mouse
 * (src/game/runtime/touch-variants.ts). Touch mode (the controls, the pause in the background, the keyboard view) starts with a
 * phone or tablet (a coarse primary pointer) or ?touch=1; a laptop with a touch screen driven by its mouse stays a
 * desktop until its first real touch.
 *
 * Query parameters: backend=<spec>, dev=1 (developer test profile for a new account, where the backend allows it: always
 * offline, on a server only at L2 with DEV_PROFILES=true), autoguest=1 (tests and development only: a guest account
 * instead of the sign-in screen, honoured only with the in-process backends offline / memory), trace=1 (log trace() output),
 * perf=1 (performance overlay; dev builds or VITE_FWP_PERF=1 builds only), touch=1 (the touch controls also without
 * a touch screen), wide=1 / wide=0 (the wide view on / off whatever the device: by default on in touch mode and in the
 * app, off on a desktop), store=preview (the shop's skeleton without the development payment provider, as players on
 * the web page see it),
 * render=nosharp,nohd,nodevcache,native (render comparisons: no sharp bilinear, no HD variants, terrain resampled
 * every frame instead of the device-resolution cache, fixed native resolution). A build without the developer hooks
 * (the Android release build, src/client/build-flags.ts) ignores the whole query and sets no window.__fwp.
 */
import { AssetLibrary, FlashPlayer, SharedObject } from './flash/index';
import { BrowserPlatform, LocalStorageStorage } from './flash/platform/browser';
import { BrowserHost, HOLD_FRAME_MS } from './flash/platform/browser-host';
import { FWP, type RestartReason } from './game/FWP';
import { initLanguage, isLanguage, language, setTouchTexts, systemLanguage, translate, type Language } from './game/runtime/i18n';
import { configureRuntime } from './game/runtime/as3';
import { configureClipboard } from './game/runtime/flash-shims';
import type { GameServices } from './client/game-services';
import { type BackendConnectionConfig, normalizeServerAddress, parseBackendSpec, startupBackendSpec } from './client/config';
import { CLEARTEXT_SERVERS, DEV_HOOKS, TEST_PROVIDERS } from './client/build-flags';
import { checkForUpdate, createMonetization, playBuildConfig, playGamesHooks } from './client/platform/monetization';
import { playPlugin } from './client/platform/play';
import { createIntegrity, createPush, createTelemetry, integrationsBuildConfig } from './client/platform/integrations';
import { type AccountLink, installDeepLinks } from './client/platform/deep-links';
import { type GameServicesConfig, defaultCredentialStore } from './client/game-services';
import { connectWithRetry, type StartupState } from './client/net/startup';
import { isNativeShell, shellPlatform } from './client/platform/capacitor';
import { closeDialog, confirmDialog, dialogOpen, noticeDialog, passwordDialog, serverDialog, setDialogKeyboard, statusButton } from './client/platform/dialogs';
import { exitApp, installLifecycle } from './client/platform/lifecycle';
import { readServerSetting, writeServerSetting } from './client/platform/server-setting';
import { type AppStorage, createAppStorage, flushWithin } from './client/platform/storage';
import { MIN_PRESS_MS, TouchControls, clusterRects, isTouchScreen, placeControls, watchFirstTouch } from './client/platform/touch-controls';
import { type ScreenInsets, watchScreenInsets } from './client/platform/viewport';
import { WIDE_MIN_WIDTH, WIDE_VIEW } from './shared/wide-view';
import * as AT from './game/runtime/auth-texts';
import { backAction, battleControls, battleFieldAt, battleHudBounds, battleMenuBounds, diamondBetAt, holdBattle, surrenderAt } from './game/runtime/mobile-port';
import { TT_BET_NO, TT_BET_YES, TT_SURRENDER_NO, TT_SURRENDER_QUESTION, TT_SURRENDER_YES } from './game/runtime/touch-texts';
import { MSG_TRADEMARK } from './game/ui/legal-notice';
import { pickAt } from './flash/player/touch-assist';
import { MSG_CONNECTING, MSG_LOADING_ASSETS, MSG_RETRY_HINT, MSG_VERSION_MISMATCH, serverAddressText, serverUnavailableText, startupFailedText } from './game/runtime/net-messages';

/** sessionStorage key of the reason of the last restart (FWPDeps.restart / restartReason). */
/** A finger that misses the game's buttons presses the one within this many CSS px (BrowserInputOptions.touchSlopPx). */
const TOUCH_SLOP_PX = 14;
const RESTART_KEY = 'fwp.restart';
const RESTART_REASONS: readonly RestartReason[] = ['language', 'signed_out', 'account_deleted', 'session_expired', 'session_replaced', 'player_changed', 'error'];

/** The app storage of this start (createAppStorage); its native writes are awaited before a restart and the exit. */
let appStorage: AppStorage | null = null;

/** Reloads the page (a clean start of the game) and keeps why for the next start. */
function restartGame(reason?: RestartReason): void {
  try {
    if (reason) sessionStorage.setItem(RESTART_KEY, reason);
  } catch {
    /* no sessionStorage: the next start just shows no reason */
  }
  // The native writes first (the new session, the server setting), but a plugin call that hangs must not stop it.
  void flushWithin(appStorage).then(() => location.reload());
}

/** The reason of the restart that led to this start (read once). */
function takeRestartReason(): RestartReason | null {
  try {
    const r = sessionStorage.getItem(RESTART_KEY);
    sessionStorage.removeItem(RESTART_KEY);
    return RESTART_REASONS.includes(r as RestartReason) ? (r as RestartReason) : null;
  } catch {
    return null;
  }
}

/** The loading screen's message, and a detail line under it in small print (the server's address). */
function setStatus(text: string, detail = ''): void {
  const el = document.getElementById('status');
  if (el) {
    el.textContent = text;
    if (detail) {
      const d = document.createElement('small');
      d.className = 'detail';
      d.textContent = detail;
      el.append(d);
    }
    el.hidden = text === '';
    // (the loading screen with the emblem: while there is something to say)
    const boot = document.getElementById('boot');
    if (boot) boot.hidden = text === '';
  }
}

/**
 * Connects to the configured backend. While it is unavailable the status line shows its address and the countdown
 * to the next attempt (1 s doubling up to 30 s); a click or a key press retries at once. A failure that waiting cannot
 * fix (an incompatible server version) stays on screen; a click then reloads the page (a new frontend version).
 */
function connectBackend(config: GameServicesConfig, onServer: (() => void) | null): Promise<GameServices> {
  let state: StartupState | null = null;
  const render = () => {
    const s = state;
    if (!s) return;
    // Where the player may pick another server: a button under the message while this one does not answer.
    statusButton(onServer && s.phase !== 'connecting' ? translate(AT.MSG_AUTH_SERVER) : null, () => onServer?.());
    // The player reads that the game's server does not answer; the address is a detail line in small print.
    const address = translate(serverAddressText(s.address));
    if (s.phase === 'connecting') setStatus(translate(s.attempt > 1 ? serverUnavailableText(0) : MSG_CONNECTING), s.attempt > 1 ? address : '');
    else if (s.phase === 'waiting') {
      const seconds = Math.max(1, Math.ceil((s.retryAt - Date.now()) / 1000));
      setStatus(translate(serverUnavailableText(seconds)) + '\n' + translate(MSG_RETRY_HINT), address);
    } else setStatus(translate(s.code === 'VERSION_MISMATCH' ? MSG_VERSION_MISMATCH : startupFailedText(s.message)) + '\n' + translate(MSG_RETRY_HINT));
  };
  const conn = connectWithRetry(config, {
    onState: (s) => {
      state = s;
      render();
    },
  });
  const timer = setInterval(render, 250);
  const retry = () => {
    if (state?.phase === 'failed' && state.code === 'VERSION_MISMATCH') location.reload();
    else conn.retryNow();
  };
  window.addEventListener('keydown', retry);
  window.addEventListener('pointerdown', retry);
  return conn.services.finally(() => {
    clearInterval(timer);
    statusButton(null, () => undefined);
    closeDialog();
    window.removeEventListener('keydown', retry);
    window.removeEventListener('pointerdown', retry);
  });
}

/**
 * Decodes the battle bundle's shape atlases while the player is in the menus, so the first aim, the launch-power bar
 * and the first explosion of a session are not drawn half-empty while their atlas page is still being fetched
 * (shapes are loaded lazily, on first draw). Battle start awaits the same loads through `preloadWithMaps`, so this
 * only moves the cost earlier. It waits for the menu's own lazy images first: starting it at boot delayed the first
 * screens instead (measured: last menu atlas ready at 15.7 s instead of 10.2 s on a throttled link).
 */
function warmBattleImages(library: AssetLibrary): void {
  const start = () => void library.preloadImages(['obj']).catch(() => {});
  let waited = 0;
  const poll = () => {
    if (library.images.pendingCount === 0 || (waited += 250) > 15000) start();
    else setTimeout(poll, 250);
  };
  setTimeout(poll, 1000);
}

/** The address shown for a backend (the server setting): http(s) for the game's WebSocket endpoint at /ws. */
function displayAddress(config: BackendConnectionConfig): string {
  if (config.transport === 'in-process') return config.storage === 'memory' ? 'memory' : 'offline';
  if (config.transport === 'http') return config.baseUrl;
  const url = new URL(config.url);
  const scheme = url.protocol === 'wss:' ? 'https:' : 'http:';
  return `${scheme}//${url.host}${url.pathname === '/ws' ? '' : url.pathname}`;
}

/** The connection config of this client: the app storage keeps the credentials (and the offline backend's data). */
function withAppStorage(config: BackendConnectionConfig, storage: AppStorage, native: boolean): GameServicesConfig {
  if (!native) return config;
  const out: GameServicesConfig = { ...config, credentials: defaultCredentialStore(config, storage), device: { platform: shellPlatform() } };
  if (out.transport === 'in-process' && (out.storage === undefined || out.storage === 'localStorage')) out.webStorage = storage;
  return out;
}

/** The stage's rectangle on the page (CSS px): the 807x600 window; beside it the letterbox bars or the wide view's margin. */
/**
 * The canvas' client rectangle, read once per game tick and view (the touch controls and the game ask for the stage's
 * place many times a frame; each read after the controls' last DOM change forces a style and layout pass).
 */
let canvasRect: { rect: DOMRect; tick: number; view: unknown; w: number; h: number } | null = null;
function canvasClientRect(host: BrowserHost, player: FlashPlayer): DOMRect {
  const c = host.canvas;
  const tick = player.frame;
  const q = canvasRect;
  if (q && q.tick === tick && q.view === host.viewport && q.w === c.width && q.h === c.height) return q.rect;
  const rect = c.getBoundingClientRect();
  canvasRect = { rect, tick, view: host.viewport, w: c.width, h: c.height };
  return rect;
}

function stageCssRect(host: BrowserHost, player: FlashPlayer): { left: number; top: number; width: number; height: number } {
  const c = host.canvas;
  const r = canvasClientRect(host, player);
  const kx = c.width > 0 ? r.width / c.width : 1;
  const ky = c.height > 0 ? r.height / c.height : 1;
  const vp = host.viewport;
  const st: any = player.stage;
  return { left: r.left + vp.tx * kx, top: r.top + vp.ty * ky, width: st.stageWidth * vp.scale * kx, height: st.stageHeight * (vp.scaleY ?? vp.scale) * ky };
}

async function boot(): Promise<void> {
  // The page query is a developer/test hook: a build without the hooks (the Android release APK) ignores it whole.
  const params = new URLSearchParams(DEV_HOOKS ? location.search : '');
  const canvas = document.getElementById('stage') as HTMLCanvasElement;
  // Notches, system bars and the on-screen keyboard, from the start: the page's own dialogs (the server form of the
  // loading screen) keep clear of the keyboard before the game is there; the host gets them once it is (onInsets).
  let screenInsets: ScreenInsets = { safe: { left: 0, top: 0, right: 0, bottom: 0 }, keyboard: 0 };
  let onInsets: ((v: ScreenInsets) => void) | null = null;
  watchScreenInsets((v) => {
    screenInsets = v;
    setDialogKeyboard(v.keyboard);
    onInsets?.(v);
  });
  // Platform adapter: Capacitor Preferences in the native app, else localStorage (src/client/platform/storage.ts).
  const storage = (appStorage = await createAppStorage());
  const native = isNativeShell();
  const platform = new BrowserPlatform({
    baseUrl: new URL('./', document.baseURI).href,
    ...(storage.kind === 'native' ? { storage: new LocalStorageStorage(storage, 'fwp:') } : {}),
  });
  configureRuntime({
    random: () => platform.random(),
    now: () => Date.now(),
    ...(params.get('trace') === '1' ? { trace: (line: string) => console.log('[trace]', line) } : {}),
  });
  configureClipboard((text) => {
    navigator.clipboard?.writeText(text).catch(() => {});
  });
  const player = new FlashPlayer(platform, { errorPolicy: 'log' });
  // Language: ?lang, else the player's choice (settings screen), else the system's language on a first start
  // (Ukrainian, Russian, or English for every other language: desktop browsers and phones alike); the catalogue must
  // be in place before the game is built, because the data tables translate weapon and perk names while they are
  // created.
  const prefs = SharedObject.getLocal('fwp_prefs');
  const asked = params.get('lang');
  const lang: Language = isLanguage(asked)
    ? asked
    : isLanguage(prefs.data.lang)
      ? prefs.data.lang
      : systemLanguage(navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language]);
  // Touch mode from the start on a phone or tablet (or ?touch=1); a touch laptop gets it at its first real touch, and so
  // does the app on a device driven by a mouse (DeX, a Chromebook): `native` alone brings the background pause and quickAdapt.
  let touchMode = isTouchScreen() || params.get('touch') === '1';
  await initLanguage(platform, lang);
  // The trademark notice under the loading screen's status, in the player's language (at every start: a restored
  // session goes from here straight to the main map, without the game's own splash).
  const notice = document.getElementById('notice');
  if (notice) notice.textContent = translate(MSG_TRADEMARK);
  // Touch mode's help texts describe the on-screen controls instead of keys and the mouse, from the first text shown.
  setTouchTexts(touchMode);
  setStatus(translate(MSG_LOADING_ASSETS));
  const library = await AssetLibrary.load(platform, 'assets', { shapes: 'lazy', maps: 'lazy', preloadWithMaps: ['obj'] });
  await platform.loadFonts(library.fonts);
  setStatus(translate(MSG_CONNECTING));
  // ?backend= names a server only on the Vite dev server: a production page never sends passwords to a server a link
  // chose (src/client/config.ts pageBackendSpec); the in-process backends (offline, memory) stay available. Then the
  // player's server setting (where offered), the build's VITE_BACKEND_URL, VITE_FWP_BACKEND, the page's origin.
  // A build without cleartext (the Android release APK: its network security config allows HTTPS only) accepts only
  // https:// / wss:// server addresses, stored or typed (src/client/build-flags.ts CLEARTEXT_SERVERS).
  const secureOnly = !CLEARTEXT_SERVERS;
  const settingOffered = native || import.meta.env.DEV === true || import.meta.env.VITE_FWP_SERVER_SETTING === '1';
  const stored = settingOffered ? readServerSetting(storage) : '';
  const backend = startupBackendSpec({
    param: params.get('backend'),
    allowAddressParam: import.meta.env.DEV === true,
    stored,
    buildUrl: import.meta.env.VITE_BACKEND_URL,
    buildSpec: import.meta.env.VITE_FWP_BACKEND,
    secureOnly,
  });
  if (backend.ignored) console.warn(`?backend=${backend.ignored} ignored: this page connects only to its own server or to an in-browser backend (offline, memory)`);
  const check = (address: string): string | null => {
    const r = normalizeServerAddress(address, { secureOnly });
    return r.ok ? null : r.problem;
  };
  const apply = (address: string): void => {
    writeServerSetting(storage, address, { secureOnly });
    restartGame();
  };
  const openServerForm = (opts: { cancel?: boolean; error?: string | null } = {}) =>
    serverDialog(
      {
        title: translate(AT.MSG_AUTH_SERVER),
        label: translate(AT.MSG_AUTH_SERVER_ADDRESS),
        hint: translate(secureOnly ? AT.MSG_AUTH_SERVER_HINT_SECURE : AT.MSG_AUTH_SERVER_HINT),
        save: translate(AT.MSG_AUTH_SERVER_SAVE),
        reset: translate(AT.MSG_AUTH_SERVER_DEFAULT),
        cancel: translate(AT.MSG_AUTH_BACK),
      },
      stored,
      (address) => {
        const problem = check(address);
        return problem ? translate(AT.SERVER_PROBLEM_TEXT[problem] ?? AT.SERVER_PROBLEM_TEXT.invalid) : null;
      },
      apply,
      opts,
    );
  // The native app's page is not served by the game's server: without a build default or a stored address it has
  // no server to talk to until the player names one.
  if (native && backend.source === 'default') {
    setStatus(translate(AT.MSG_SERVER_NOT_SET));
    openServerForm({ cancel: false });
    return;
  }
  const config = parseBackendSpec(backend.spec);
  const services = await connectBackend(withAppStorage(config, storage, native), settingOffered && backend.source !== 'param' ? () => openServerForm() : null);
  // The developer test profile (level 10, 5 000 000 gold, every weapon): `?dev=1` asks the backend for it when it
  // creates the profile (profile.load { dev: true }); the backend decides (the offline one always, a self-hosted server
  // at L2 only with DEV_PROFILES=true). Where the game creates its own state (L0/L1) it builds the profile only with
  // the in-process backend (FWP.net_load_profile), so a shared server never publishes cheat profiles to the tops.
  // Google Play (Android app: the FwpPlay plugin) or the development providers (dev builds): the store, the opt-in
  // rewarded ads, Play Games, review and updates (src/client/platform/monetization.ts). The web page and a build or a
  // server without payments get the store's skeleton only (its offers; a purchase says payment is not connected yet);
  // `?store=preview` shows that skeleton in a development build too.
  const play = playPlugin();
  const playConfig = playBuildConfig(import.meta.env as unknown as Record<string, string | undefined>);
  const monetization = createMonetization({
    services,
    play,
    testProviders: TEST_PROVIDERS,
    storePreview: DEV_HOOKS && params.get('store') === 'preview',
    config: playConfig,
    confirmTestAd: () => confirmDialog({ question: 'Test ad (development build): count it as watched?', yes: 'OK', no: 'Cancel' }),
    storage: { get: (k) => storage.getItem(k), set: (k, v) => storage.setItem(k, v) },
    restart: () => restartGame(),
  });
  if (play) {
    // Play Games achievements and leaderboards from the game's own command results.
    const hooks = playGamesHooks(play, playConfig);
    const command = services.command.bind(services);
    services.command = async (op, args, opts) => {
      const out = await command(op, args, opts);
      hooks.onCommand(op, out.result);
      return out;
    };
    void checkForUpdate(play);
  }
  // Play Integrity, diagnostics and push notifications (src/client/platform/integrations.ts): no-ops on the web page and
  // in a build or with a server without them; diagnostics and notifications stay off until the player turns them on.
  const integrationsConfig = integrationsBuildConfig(import.meta.env as unknown as Record<string, string | undefined>);
  const kv = { get: (k: string) => storage.getItem(k), set: (k: string, v: string) => storage.setItem(k, v) };
  const integrity = createIntegrity({ play, services, config: integrationsConfig });
  const telemetry = createTelemetry({ play, config: integrationsConfig, storage: kv });
  const push = createPush({ play, config: integrationsConfig, services, storage: kv, lang: () => language(), account: () => services.getIdentity()?.playerId ?? null });
  // Errors of the page as non-fatal Crashlytics reports (only with diagnostics on; addresses stripped).
  window.addEventListener('error', (e) => telemetry.recordError(e.error ?? e.message));
  window.addEventListener('unhandledrejection', (e) => telemetry.recordError(e.reason));
  watchSignIns(services, (method) => {
    void integrity.check('sign_in');
    void push.afterSignIn();
    telemetry.logEvent(method === 'sign_up' ? 'sign_up' : 'login', { method: method === 'sign_up' ? 'game' : method });
  });
  const signOut = services.signOut.bind(services);
  services.signOut = async () => {
    await push.beforeSignOut();
    return signOut();
  };
  const store = monetization?.store;
  if (store) {
    const buy = store.buy.bind(store);
    // The integrity signal accompanies the purchase on the server; it never holds the purchase up.
    store.buy = (productId) => {
      void integrity.check('purchase');
      return buy(productId);
    };
  }
  const game = new FWP({
    library,
    services,
    monetization,
    integrations: { telemetry, push },
    devProfile: params.get('dev') === '1',
    restart: restartGame,
    restartReason: takeRestartReason(),
    // Monotonic real time for the backend bridge (call watchdog, retry backoff).
    netClock: () => performance.now(),
    // The sign-in screen's "Server" view (not for a backend the page's link chose).
    serverSetting: settingOffered && backend.source !== 'param' ? { current: displayAddress(config), stored, check, apply, secureOnly } : null,
  });
  // The wide view: in touch mode and in the app (a phone, a tablet), unless the page asks otherwise (?wide=1 / 0).
  const wideParam = params.get('wide');
  const wideOn = (): boolean => (wideParam === '1' ? true : wideParam === '0' ? false : native || touchMode);
  // Dev/diagnostic render switches (visual comparisons): ?render=nosharp,nohd,nodevcache,native,nodamage
  const renderFlags = new Set((params.get('render') ?? '').split(',').filter(Boolean));
  const host = new BrowserHost({
    player,
    canvas,
    platform,
    library,
    renderer: { sharpBilinear: !renderFlags.has('nosharp'), disableHd: renderFlags.has('nohd'), deviceBitmapCache: !renderFlags.has('nodevcache'), damage: !renderFlags.has('nodamage') },
    ...(renderFlags.has('native') ? { adaptiveResolution: false } : {}),
    // A phone without the wide view zooms into the sign-in form and the account dialogs (the whole stage would make
    // their text a few pixels tall; a wide screen shows the dialogs larger itself).
    focusRect: () =>
      game.auth_screen
        ? game.auth_screen.focusRect()
        : game.delete_dialog
          ? game.delete_dialog.focusRect()
          : game.email_dialog
            ? game.email_dialog.focusRect()
            : game.account_dialog
              ? game.account_dialog.focusRect()
              : game.chat_ui
                ? game.chat_ui.focusRect()
                : null,
    // A tapped control keeps its hover (the game's tooltips) for a moment after the finger is lifted.
    touchHoverMs: 1500,
    // Fingers on the 800x600 UI (buttons of 10-30 CSS px on a phone): a miss presses the one button within 14 CSS px,
    // a long press shows the hover hints without a click; the battle's map keeps exact taps. The mouse is unchanged.
    // A finger that missed a touch control by less than the slop gets no slop either: a near miss of the bounce button
    // never presses the scanner beside it (which spends a scanner) or surrender.
    touchSlopPx: TOUCH_SLOP_PX,
    longPressMs: 450,
    touchAssistAt: (target, x, y) => !battleFieldAt(game, target, x, y) && !nearTouchControl(x, y),
    // A finger on the battle's surrender button (next to the weapon sheet's, 24 CSS px on a phone) asks first; on yes
    // the button gets its tap, like the back button's taps (the game's own surrender, recorded as always). So does a
    // diamond bet of the roulette (one tap spent the diamonds at once): the question is the bet's own text.
    holdTap: (target, x, y) => {
      const bet = diamondBetAt(game, target);
      if (bet) {
        void confirmDialog({ question: bet.question, yes: translate(TT_BET_YES), no: translate(TT_BET_NO) }).then((yes) => {
          if (!yes || !diamondBetAt(game, pickAt(player.stage, x, y))) return;
          for (const type of ['move', 'down', 'up', 'leave'] as const) player.injectMouse(type, x, y);
          host.requestRender();
        });
        return true;
      }
      if (!surrenderAt(game, target)) return false;
      // A solo battle waits while the player reads the question (its turn clock too); an online one runs on.
      const release = holdBattle(game);
      void confirmDialog({ question: translate(TT_SURRENDER_QUESTION), yes: translate(TT_SURRENDER_YES), no: translate(TT_SURRENDER_NO) }).then((yes) => {
        release();
        if (!yes || !surrenderAt(game, pickAt(player.stage, x, y))) return;
        for (const type of ['move', 'down', 'up', 'leave'] as const) player.injectMouse(type, x, y);
        host.requestRender();
      });
      return true;
    },
    // Phones and the app (a touch laptop from its first touch): on the menus dynamic resolution keeps the text at
    // least at the original's own resolution (1 device px per stage px); a battle may still go down to the CSS size
    // (its frame rate first).
    minStageScale: () => ((native || touchMode) && game.main_mode !== 'LOCAL_GAME' ? 1 : 0),
    // Phones, tablets and the app show the margin around the window instead of letterbox bars.
    wide: () => (wideOn() ? { ...WIDE_VIEW, minWidth: WIDE_MIN_WIDTH } : null),
    // Phones, tablets and the app, on the menus: a screen whose art is still loading (its panels, the wide margin's
    // art: lazily decoded images) is shown once it is whole, the last frame stays meanwhile (at most HOLD_FRAME_MS).
    // A battle's frames are shown as they are (its art is preloaded with the map); a desktop as before.
    holdFrame: () => wideOn() && game.main_mode !== 'LOCAL_GAME',
    // Phones and the app: dynamic resolution reacts within ~20 slow frames (desktop keeps its slower ladder).
    quickAdapt: native || touchMode,
    touchScreen: touchMode,
  });
  // Notches, system bars and the on-screen keyboard (the canvas keeps its size under the keyboard).
  onInsets = (v) => {
    host.setSafeInsets(v.safe);
    host.setKeyboardInset(v.keyboard);
  };
  onInsets(screenInsets);
  player.setRoot(game);
  host.start();
  player.start();
  // The account links of the game's e-mails the app was opened with (Android App Links: src/client/platform/deep-links.ts).
  installDeepLinks((link) => void openAccountLink(services, link));
  // Battle controls of touch screens: the keyboard's keys through the keyboard's injection path.
  let touch: TouchControls | null = null;
  /** Stage point (x, y) is within the touch slop of a shown touch control (the controls' own near misses). */
  function nearTouchControl(x: number, y: number): boolean {
    if (!touch) return false;
    const r = stageCssRect(host, player);
    const k = r.width / (player.stage as any).stageWidth;
    return k > 0 && touch.near(r.left + x * k, r.top + y * k, TOUCH_SLOP_PX);
  }
  const enableTouch = (): void => {
    if (touch) return;
    touchMode = true;
    setTouchTexts(true);
    host.setTouchScreen(true);
    const controls = (touch = new TouchControls({
      key: (type, keyCode, charCode) => {
        player.injectKey(type, keyCode, charCode, {});
        host.requestRender();
      },
      state: () => (host.isPaused ? null : battleControls(game)),
      stageRect: () => stageCssRect(host, player),
      safeInsets: () => screenInsets.safe,
      // The battle HUD's icons (scanner, weapon sheet, surrender): the wide screen's HUD keeps clear of the buttons
      // (touch_zones below); without a wide view a tablet's buttons keep clear of the icons.
      keepClear: () => {
        const r = stageCssRect(host, player);
        const st: any = player.stage;
        const k = r.width / st.stageWidth;
        return battleHudBounds(game).map((b) => ({ left: r.left + b.x * k, top: r.top + b.y * k, width: b.width * k, height: b.height * k }));
      },
      // The weapon sheet's footer row, the scanner's room beside its tabs (their touch layouts keep it for the menus' info).
      menuInfoAt: () => {
        const l = game.menu_info_slot();
        if (!l) return null;
        const r = stageCssRect(host, player);
        const k = r.width / (player.stage as any).stageWidth;
        return { left: r.left + l.x * k, top: r.top + l.y * k, width: l.width * k, height: l.height * k, hidden: l.covered };
      },
      // The open menu's cells (the weapon sheet's): the menus' info narrows beside them rather than covering them.
      menuKeepClear: () => {
        const r = stageCssRect(host, player);
        const st: any = player.stage;
        const k = r.width / st.stageWidth;
        return battleMenuBounds(game).map((b) => ({ left: r.left + b.x * k, top: r.top + b.y * k, width: b.width * k, height: b.height * k }));
      },
      // A target's confirmation: the mouse's double click where the game's cursor is (the finger's last point).
      mouse: (type, x, y) => {
        player.injectMouse(type, x, y);
        host.requestRender();
      },
      pointer: () => {
        const st: any = player.stage;
        const r = stageCssRect(host, player);
        const [x, y] = [st.mouseX, st.mouseY];
        return { x, y, clientX: r.left + (x * r.width) / st.stageWidth, clientY: r.top + (y * r.height) / st.stageHeight };
      },
      onMap: (cx, cy) => {
        if (document.elementFromPoint(cx, cy) !== canvas) return false; // a dialog over the battle
        const [x, y] = host.toStage(cx, cy);
        return battleFieldAt(game, pickAt(player.stage, x, y), x, y);
      },
      back: () => back(),
      // Under the battle's team bars (the wide screen's HUD), else the stage's top.
      hintTop: () => {
        const b = game.wide_screen?.hudRect('teams');
        if (!b) return null;
        const r = stageCssRect(host, player);
        return r.top + ((b.y + b.height) * r.height) / (player.stage as any).stageHeight + 8;
      },
      text: translate,
      vibrate: (ms) => {
        navigator.vibrate?.(ms);
      },
    }));
    // A physical keyboard in use hides them until the next touch.
    window.addEventListener('keydown', (e) => {
      const t = e.target as HTMLElement | null;
      if (e.isTrusted && !(t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA'))) controls.setKeyboardInUse(true);
    });
    window.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch') controls.setKeyboardInUse(false);
    }, true);
    const prevFrame = player.onFrame;
    player.onFrame = (p, n) => {
      if (prevFrame) prevFrame(p, n);
      controls.update();
    };
    // Where the controls' clusters sit on this screen (stage coordinates; their place without the HUD, which then
    // keeps clear of them: src/game/ui/wide-screen.ts): the battle HUD moves out of their way and drops its backpack.
    game.touch_zones = () => {
      const r = stageCssRect(host, player);
      const st: any = player.stage;
      const k = r.width / st.stageWidth;
      if (!(k > 0)) return [];
      return clusterRects(placeControls(window.innerWidth, window.innerHeight, r, screenInsets.safe)).map((c) => ({
        x: (c.left - r.left) / k,
        y: (c.top - r.top) / k,
        width: c.width / k,
        height: c.height / k,
      }));
    };
    // Whether they are shown now (hidden during the opponent's turn): the connection overlay's notice keeps clear of
    // them only then.
    game.touch_shown = () => controls.state !== null;
    // A target mode before the finger chose its point: the game's cursor is not drawn yet.
    game.touch_target_pending = () => controls.targetPending;
    // The buttons shown now, while the player aims: the camera keeps the current worm clear of those that come.
    game.touch_buttons = () => {
      const s = controls.state;
      const r = stageCssRect(host, player);
      const k = r.width / (player.stage as any).stageWidth;
      if (!s || !(k > 0)) return null;
      return { mode: s.mode, rects: controls.shownRects().map((c) => ({ x: (c.left - r.left) / k, y: (c.top - r.top) / k, width: c.width / k, height: c.height / k })) };
    };
    // The close button of the battle's menus (the weapon button's place): theirs lie under it.
    game.touch_close_rect = () => {
      const b = controls.slotRect('weapon');
      const r = stageCssRect(host, player);
      const k = r.width / (player.stage as any).stageWidth;
      return b && k > 0 ? { x: (b.left - r.left) / k, y: (b.top - r.top) / k, width: b.width / k, height: b.height / k } : null;
    };
    game.wide_screen?.refresh();
  };
  if (touchMode) enableTouch();
  else watchFirstTouch(enableTouch);
  // The Android back button: the top screen's close button, else the question whether to leave the game.
  const back = (): void => {
    if (dialogOpen()) return closeDialog();
    const result = backAction(game, {
      tap: (x, y) => {
        for (const type of ['move', 'down', 'up', 'leave'] as const) player.injectMouse(type, x, y);
        host.requestRender();
      },
      key: (keyCode) => {
        player.injectKey('down', keyCode, keyCode === 27 ? 27 : 0, {});
        setTimeout(() => player.injectKey('up', keyCode, 0, {}), MIN_PRESS_MS);
      },
    });
    if (result === 'exit') {
      void confirmDialog({ question: translate(AT.MSG_EXIT_QUESTION), yes: translate(AT.MSG_EXIT_YES), no: translate(AT.MSG_EXIT_NO) }).then(async (yes) => {
        if (!yes) return;
        // Writes still queued for the native store would be lost with the activity: wait for them (bounded).
        SharedObject.flushAll();
        await flushWithin(storage);
        exitApp();
      });
    }
  };
  // Background / foreground: phones and the app pause the game (loop, audio) and every client wakes its connection
  // on return (an immediate reconnect and re-authentication; a desktop tab keeps running as before).
  const lifecycle = installLifecycle({
    onPause: () => {
      if (!(native || touchMode)) return;
      touch?.releaseAll();
      host.pause();
      void storage.flush();
    },
    onResume: () => {
      host.resume();
      void services.client.wake().catch(() => undefined);
    },
    onBack: back,
  });
  // Dev-only performance overlay (`?perf=1`); compiled out of production builds unless VITE_FWP_PERF=1.
  if (params.get('perf') === '1' && (import.meta.env.DEV || import.meta.env.VITE_FWP_PERF === '1')) {
    const { installPerfOverlay } = await import('./perf/overlay');
    (globalThis as any).__fwpPerf = installPerfOverlay({ player, host, library, platform });
  }
  // touch and touchMode are getters: a touch laptop gets its controls at the first real touch.
  if (DEV_HOOKS) (globalThis as any).__fwp = { player, game, library, services, host, get touch() { return touch; }, get touchMode() { return touchMode; }, get language() { return language(); }, lifecycle, back, storage };
  warmBattleImages(library);
  // The loading screen stays over the canvas until the game's first frame is drawn (on a phone the host waits for the
  // first screen's art, BrowserHost holdFrame): no texts and icons of the main map on a black screen before its
  // picture. (At most a little longer than the host waits.)
  const until = performance.now() + HOLD_FRAME_MS + 500;
  while (!host.frameShown && performance.now() < until) await new Promise((r) => setTimeout(r, 30));
  setStatus('');
}

boot().catch((error) => {
  console.error(error);
  setStatus(translate(startupFailedText(error instanceof Error ? error.message : String(error))));
});

/** Calls `after` once a sign-in of the game's services succeeded ('sign_up', 'nickname', 'email', 'play_games', 'restore'). */
function watchSignIns(services: GameServices, after: (method: string) => void): void {
  const wrap = <K extends 'signUp' | 'signIn' | 'signUpWithEmail' | 'signInWithEmail' | 'signInWithPlayGames' | 'restoreSession'>(name: K, method: string) => {
    const original = (services[name] as (...a: unknown[]) => Promise<unknown>).bind(services);
    (services as unknown as Record<string, unknown>)[name] = async (...args: unknown[]) => {
      const r = await original(...args);
      if (r) after(method);
      return r;
    };
  };
  wrap('signUp', 'sign_up');
  wrap('signUpWithEmail', 'sign_up');
  wrap('signIn', 'nickname');
  wrap('signInWithEmail', 'email');
  wrap('signInWithPlayGames', 'play_games');
  wrap('restoreSession', 'restore');
}

/**
 * An account link of an e-mail opened in the app: a confirmation link confirms the address (auth.emailVerify) and
 * says so; a reset link asks for the new password (auth.passwordReset: every session of the account ends, so the
 * game returns to its sign-in screen when it was signed in to that account).
 */
async function openAccountLink(services: GameServices, link: AccountLink): Promise<void> {
  const reasonOf = (err: unknown) => String((err as { details?: { reason?: unknown } })?.details?.reason ?? '');
  const linkError = (err: unknown) => {
    const reason = reasonOf(err);
    return translate(reason === 'email_token_invalid' ? AT.MSG_LINK_INVALID : reason === 'email_taken' ? AT.MSG_LINK_TAKEN : AT.MSG_LINK_FAILED);
  };
  if (link.action === 'verify') {
    try {
      const email = await services.verifyEmail(link.token);
      await noticeDialog({ title: translate(AT.MSG_LINK_VERIFIED_TITLE), text: `${translate(AT.MSG_LINK_VERIFIED)} ${email}`, ok: translate(AT.MSG_LINK_OK) });
    } catch (err) {
      await noticeDialog({ title: translate(AT.MSG_LINK_TITLE), text: linkError(err), ok: translate(AT.MSG_LINK_OK) });
    }
    return;
  }
  passwordDialog(
    {
      title: translate(AT.MSG_LINK_RESET_TITLE),
      label: translate(AT.MSG_LINK_RESET_TITLE),
      repeat: translate(AT.MSG_LINK_RESET_REPEAT),
      mismatch: translate(AT.MSG_LINK_RESET_MISMATCH),
      hint: translate(AT.MSG_LINK_RESET_HINT),
      save: translate(AT.MSG_LINK_RESET_SAVE),
      cancel: translate(AT.MSG_AUTH_BACK),
      wait: translate(AT.MSG_AUTH_WAIT),
    },
    async (password) => {
      try {
        await services.resetPassword(link.token, password);
      } catch (err) {
        const reason = reasonOf(err);
        if (reason in AT.CREDENTIAL_PROBLEM_TEXT) return translate(AT.CREDENTIAL_PROBLEM_TEXT[reason as keyof typeof AT.CREDENTIAL_PROBLEM_TEXT]);
        return linkError(err);
      }
      void noticeDialog({ title: translate(AT.MSG_LINK_RESET_TITLE), text: translate(AT.MSG_LINK_RESET_DONE), ok: translate(AT.MSG_LINK_OK) });
      return null;
    },
  );
}
